// Salesforce 接続タブ。OAuth フロー開始 / 接続状態表示 / 接続テスト / 切断 を担う。
// deps 経由で apiClient・redirect・search を注入し、テスト可能にする。
// 接続前はサンプルデータのまま「サンプルデータ表示中」と明示する（mock honesty）。
import { createApiClient } from "../api/client";
import { config } from "../config";
import { showToast } from "../ui/toast";
import { confirmModal } from "../ui/modal";
import { createBadge } from "../ui/badge";
import {
  start,
  getConnection,
  test as testConnection,
  disconnect as disconnectConnection,
  type ConnectApiDeps,
  type ConnectionStatus,
} from "./connect-api";

function defaultApiClient(): ConnectApiDeps["apiClient"] {
  return createApiClient({
    baseUrl: config.apiBaseUrl,
    getAccessToken: () => sessionStorage.getItem("cc.accessToken"),
    onUnauthorized: () => {
      sessionStorage.removeItem("cc.accessToken");
      window.location.hash = "#/login";
    },
  });
}

export interface ConnectDeps {
  readonly apiClient?: ConnectApiDeps["apiClient"];
  // window.location.assign のテスト代替
  readonly redirect?: (url: string) => void;
  // URLクエリ文字列 (window.location.search の代替)
  readonly search?: string;
}

const NOTE_TEXT =
  "接続資格情報は write-only（書込専用）で保存し、参照APIは提供しません（設計 §3.5 / R21）。";

export function renderConnect(root: HTMLElement, deps?: ConnectDeps): void {
  root.replaceChildren();

  const apiClient = deps?.apiClient ?? defaultApiClient();
  const apiDeps: ConnectApiDeps = { apiClient };
  const redirect = deps?.redirect ?? ((url: string) => window.location.assign(url));
  const search = deps?.search ?? window.location.search;

  const host = document.createElement("div");
  host.className = "max-w-lg space-y-4";
  root.append(host);

  const note = document.createElement("p");
  note.className = "text-sm text-slate-500";
  note.textContent = NOTE_TEXT;

  // ?connected=salesforce / ?connected=error を検出してトースト表示し、パラメータを除去する。
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const connectedParam = params.get("connected");
  if (connectedParam === "salesforce") {
    showToast({ tone: "success", title: "Salesforce 接続が完了しました" });
    // クエリパラメータをクリア（ハッシュルーティングなのでパスは変えず検索文字列だけ除去）
    const cleanUrl =
      window.location.pathname + window.location.hash;
    history.replaceState(null, "", cleanUrl);
  } else if (connectedParam === "error") {
    const reason = params.get("reason");
    const REASON_MSG: Record<string, string> = {
      access_denied: "認証がキャンセルされました。",
      app_blocked: "Connected App が組織で未承認です。管理者の承認が必要です。",
      invalid_grant: "認証に失敗しました。時間をおいて再度お試しください。",
    };
    const msg =
      reason !== null && REASON_MSG[reason] !== undefined
        ? REASON_MSG[reason]
        : "もう一度お試しください。";
    showToast({ tone: "danger", title: "Salesforce 接続に失敗しました", message: msg });
    const cleanUrl =
      window.location.pathname + window.location.hash;
    history.replaceState(null, "", cleanUrl);
  }

  // ローディングプレースホルダーを出しつつ接続状態を取得する
  const loadingText = document.createElement("p");
  loadingText.className = "text-sm text-slate-500";
  loadingText.textContent = "接続状態を確認中...";
  host.append(loadingText);

  void getConnection(apiDeps).then((status) => {
    loadingText.remove();
    if (status.status === "connected") {
      renderConnected(host, note, status, apiDeps, redirect);
    } else if (status.status === "expired") {
      renderExpired(host, note, apiDeps, redirect);
    } else {
      // "disconnected"（未接続。バックエンド runView の既定値）
      renderDisconnected(host, note, apiDeps, redirect);
    }
  }).catch(() => {
    // API 疎通できない場合は未接続として扱い、サンプル表示の旨を出す
    loadingText.remove();
    renderDisconnected(host, note, apiDeps, redirect);
  });
}

function renderDisconnected(
  host: HTMLElement,
  note: HTMLElement,
  apiDeps: ConnectApiDeps,
  redirect: (url: string) => void,
): void {
  host.replaceChildren();

  const card = document.createElement("div");
  card.className = "rounded-lg border border-slate-200 p-4";

  const title = document.createElement("h2");
  title.className = "text-base font-medium text-slate-900";
  title.textContent = "Salesforce と接続";

  const desc = document.createElement("p");
  desc.className = "mt-1 text-sm text-slate-700";
  desc.textContent =
    "OAuth（管理者同意）で接続します。クリック → 認証 → 完了の最短手数です。";

  const guide = document.createElement("p");
  guide.className = "mt-2 text-xs text-slate-500";
  guide.textContent =
    "API 有効・読み取り権限のあるユーザーで認証してください。初回は管理者の同意が必要な場合があります。";

  // 組織タイプ選択
  const orgTypeLabel = document.createElement("label");
  orgTypeLabel.className = "mt-3 block text-xs font-medium text-slate-700";
  orgTypeLabel.textContent = "組織タイプ";

  const orgSelect = document.createElement("select");
  orgSelect.dataset["testid"] = "org-type";
  orgSelect.className =
    "mt-1 block w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 focus:border-shinonome-700 focus:outline-none";

  const orgOptions: Array<[string, string]> = [
    ["production", "本番 (production)"],
    ["sandbox", "Sandbox"],
    ["mydomain", "My Domain"],
  ];
  for (const [value, label] of orgOptions) {
    const opt = document.createElement("option");
    opt.value = value;
    opt.textContent = label;
    orgSelect.append(opt);
  }

  // My Domain 入力（mydomain 選択時のみ表示）
  const myDomainInput = document.createElement("input");
  myDomainInput.type = "text";
  myDomainInput.dataset["testid"] = "my-domain";
  myDomainInput.placeholder = "例: acme.my.salesforce.com";
  myDomainInput.className =
    "mt-2 block w-full rounded border border-slate-300 px-2 py-1.5 text-sm text-slate-900 focus:border-shinonome-700 focus:outline-none hidden";

  orgSelect.addEventListener("change", () => {
    if (orgSelect.value === "mydomain") {
      myDomainInput.classList.remove("hidden");
    } else {
      myDomainInput.classList.add("hidden");
    }
  });

  const sampleNote = document.createElement("p");
  sampleNote.className = "mt-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800";
  sampleNote.textContent = "サンプルデータ表示中 — Salesforce に接続するまで監査データはサンプルです。";

  const connectBtn = document.createElement("button");
  connectBtn.type = "button";
  connectBtn.dataset["testid"] = "connect-btn";
  connectBtn.className =
    "mt-3 rounded bg-shinonome-700 px-4 py-2 text-sm font-medium text-white hover:bg-shinonome-800";
  connectBtn.textContent = "Salesforce と接続";

  connectBtn.addEventListener("click", () => {
    connectBtn.disabled = true;
    connectBtn.textContent = "認証URLを取得中...";
    const orgType = orgSelect.value;
    const body =
      orgType === "mydomain"
        ? { orgType, myDomain: myDomainInput.value.trim() }
        : { orgType };
    void start(apiDeps, body)
      .then((result) => {
        redirect(result.authorizeUrl);
      })
      .catch(() => {
        connectBtn.disabled = false;
        connectBtn.textContent = "Salesforce と接続";
        showToast({ tone: "danger", title: "接続開始に失敗しました", message: "しばらくしてからお試しください。" });
      });
  });

  card.append(title, desc, guide, orgTypeLabel, orgSelect, myDomainInput, sampleNote, connectBtn);
  host.append(card, note);
}

function renderExpired(
  host: HTMLElement,
  note: HTMLElement,
  apiDeps: ConnectApiDeps,
  redirect: (url: string) => void,
): void {
  host.replaceChildren();

  const card = document.createElement("div");
  card.className = "rounded-lg border border-amber-200 p-4";

  const titleRow = document.createElement("div");
  titleRow.className = "flex items-center gap-2";

  const title = document.createElement("h2");
  title.className = "text-base font-medium text-slate-900";
  title.textContent = "Salesforce 接続";

  const badge = createBadge({ label: "再接続が必要", tone: "warning" });
  titleRow.append(title, badge);

  const desc = document.createElement("p");
  desc.className = "mt-2 text-sm text-slate-700";
  desc.textContent = "OAuth トークンの有効期限が切れています。再認証してください。";

  // 組織タイプ選択（再接続時も指定可）
  const orgTypeLabel = document.createElement("label");
  orgTypeLabel.className = "mt-3 block text-xs font-medium text-slate-700";
  orgTypeLabel.textContent = "組織タイプ";

  const orgSelect = document.createElement("select");
  orgSelect.dataset["testid"] = "org-type";
  orgSelect.className =
    "mt-1 block w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 focus:border-shinonome-700 focus:outline-none";

  const orgOptions: Array<[string, string]> = [
    ["production", "本番 (production)"],
    ["sandbox", "Sandbox"],
    ["mydomain", "My Domain"],
  ];
  for (const [value, label] of orgOptions) {
    const opt = document.createElement("option");
    opt.value = value;
    opt.textContent = label;
    orgSelect.append(opt);
  }

  // My Domain 入力（mydomain 選択時のみ表示）
  const myDomainInput = document.createElement("input");
  myDomainInput.type = "text";
  myDomainInput.dataset["testid"] = "my-domain";
  myDomainInput.placeholder = "例: acme.my.salesforce.com";
  myDomainInput.className =
    "mt-2 block w-full rounded border border-slate-300 px-2 py-1.5 text-sm text-slate-900 focus:border-shinonome-700 focus:outline-none hidden";

  orgSelect.addEventListener("change", () => {
    if (orgSelect.value === "mydomain") {
      myDomainInput.classList.remove("hidden");
    } else {
      myDomainInput.classList.add("hidden");
    }
  });

  const reconnectBtn = document.createElement("button");
  reconnectBtn.type = "button";
  reconnectBtn.dataset["testid"] = "connect-btn";
  reconnectBtn.className =
    "mt-3 rounded bg-shinonome-700 px-4 py-2 text-sm font-medium text-white hover:bg-shinonome-800";
  reconnectBtn.textContent = "Salesforce と再接続";

  reconnectBtn.addEventListener("click", () => {
    reconnectBtn.disabled = true;
    reconnectBtn.textContent = "認証URLを取得中...";
    const orgType = orgSelect.value;
    const body =
      orgType === "mydomain"
        ? { orgType, myDomain: myDomainInput.value.trim() }
        : { orgType };
    void start(apiDeps, body)
      .then((result) => {
        redirect(result.authorizeUrl);
      })
      .catch(() => {
        reconnectBtn.disabled = false;
        reconnectBtn.textContent = "Salesforce と再接続";
        showToast({ tone: "danger", title: "接続開始に失敗しました", message: "しばらくしてからお試しください。" });
      });
  });

  card.append(titleRow, desc, orgTypeLabel, orgSelect, myDomainInput, reconnectBtn);
  host.append(card, note);
}

function renderConnected(
  host: HTMLElement,
  note: HTMLElement,
  status: ConnectionStatus,
  apiDeps: ConnectApiDeps,
  redirect: (url: string) => void,
): void {
  host.replaceChildren();

  const card = document.createElement("div");
  card.className = "rounded-lg border border-slate-200 p-4";

  const titleRow = document.createElement("div");
  titleRow.className = "flex items-center gap-2";

  const title = document.createElement("h2");
  title.className = "text-base font-medium text-slate-900";
  title.textContent = "Salesforce 接続済み";

  const badge = createBadge({ label: "接続中", tone: "success" });
  titleRow.append(title, badge);

  // 組織表示名（orgName）は SP1 対象外。Org ID と インスタンス URL のみ表示する（SP2 で org 名を取得）。
  const orgLine = document.createElement("p");
  orgLine.className = "mt-2 text-sm text-slate-700";
  orgLine.dataset["testid"] = "org-id";
  orgLine.textContent =
    status.orgId !== undefined && status.orgId !== ""
      ? "Org ID: " + status.orgId
      : "組織情報を取得できませんでした";

  const instanceLine = document.createElement("p");
  instanceLine.className = "text-sm text-slate-500";
  if (status.instanceUrl !== undefined) {
    instanceLine.dataset["testid"] = "instance-url";
    instanceLine.textContent = "インスタンス URL: " + status.instanceUrl;
  }

  const connectedAtLine = document.createElement("p");
  connectedAtLine.className = "text-sm text-slate-600";
  if (status.connectedAt !== undefined) {
    // connectedAt はバックエンドが Date.now() で書く epoch MS。秒換算（* 1000）は不要。
    const d = new Date(status.connectedAt);
    connectedAtLine.textContent =
      "接続日時: " + d.toISOString().slice(0, 16).replace("T", " ") + " UTC";
  }

  const btns = document.createElement("div");
  btns.className = "mt-4 flex gap-2";

  const testBtn = document.createElement("button");
  testBtn.type = "button";
  testBtn.dataset["testid"] = "test-btn";
  testBtn.className =
    "rounded border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100";
  testBtn.textContent = "接続テスト";

  testBtn.addEventListener("click", () => {
    testBtn.disabled = true;
    testBtn.textContent = "テスト中...";
    void testConnection(apiDeps)
      .then((result) => {
        testBtn.disabled = false;
        testBtn.textContent = "接続テスト";
        const ok = result.status === "connected";
        showToast({
          tone: ok ? "success" : "warning",
          title: ok ? "接続テスト成功" : "接続テスト: " + result.status,
        });
      })
      .catch(() => {
        testBtn.disabled = false;
        testBtn.textContent = "接続テスト";
        showToast({ tone: "danger", title: "接続テストに失敗しました" });
      });
  });

  const disconnectBtn = document.createElement("button");
  disconnectBtn.type = "button";
  disconnectBtn.dataset["testid"] = "disconnect-btn";
  disconnectBtn.className =
    "rounded border border-red-300 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50";
  disconnectBtn.textContent = "切断";

  disconnectBtn.addEventListener("click", () => {
    confirmModal({
      title: "Salesforce 接続を切断",
      message:
        "切断すると監査データの取得が停止します。再接続するには再度 OAuth 認証が必要です。切断しますか？",
      confirmLabel: "切断する",
      cancelLabel: "キャンセル",
      tone: "danger",
      onConfirm: () => {
        void disconnectConnection(apiDeps)
          .then(() => {
            showToast({ tone: "success", title: "Salesforce 接続を切断しました" });
            renderDisconnected(host, note, apiDeps, redirect);
          })
          .catch(() => {
            showToast({ tone: "danger", title: "切断に失敗しました", message: "しばらくしてからお試しください。" });
          });
      },
    });
  });

  btns.append(testBtn, disconnectBtn);
  card.append(titleRow, orgLine, instanceLine, connectedAtLine, btns);
  host.append(card, note);
}
